import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import * as metadataService from '../../services/metadataService';
import { useConfirmation } from '../../contexts/ConfirmationContext';
import { useNotification } from '../../components/framework/NotificationContext';
import VocabularyProposalsPanel from '../../components/admin/VocabularyProposalsPanel';

const MetadataManagement = () => {
    const confirm = useConfirmation();
    const [activeTab, setActiveTab] = useState('polymers');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { success, error: notifyError } = useNotification();
    const [showModal, setShowModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);

    const tabs = [
        { id: 'polymers', label: 'Polymers', icon: 'bi-pentagon' },
        { id: 'solvents', label: 'Solvents', icon: 'bi-droplet' },
        { id: 'morphologies', label: 'Morphologies', icon: 'bi-diagram-3' },
        { id: 'additives', label: 'Additives', icon: 'bi-capsule' },
        { id: 'monomers', label: 'Monomers', icon: 'bi-hexagon' },
        { id: 'proposals', label: 'Proposals', icon: 'bi-inbox' },
    ];

    useEffect(() => {
        if (activeTab !== 'proposals') loadItems();
    }, [activeTab]);

    const loadItems = async () => {
        setLoading(true);
        setError(null);
        try {
            let data = [];
            switch (activeTab) {
                case 'polymers': data = await metadataService.getAllPolymers(); break;
                case 'solvents': data = await metadataService.getAllSolvents(); break;
                case 'morphologies': data = await metadataService.getAllMorphologies(); break;
                case 'additives': data = await metadataService.getAllAdditives(); break;
                case 'monomers': data = await metadataService.getAllMonomers(); break;
            }
            setItems(data || []);
        } catch (err) {
            setError(err.message || `Failed to fetch ${activeTab}`);
        } finally {
            setLoading(false);
        }
    };

    const idOf = (item) => item.polymerId || item.solventId || item.morphologyId || item.additiveId || item.monomerId;

    const handleDelete = async (id) => {
        const isConfirmed = await confirm({
            title: `Delete ${activeTab.slice(0, -1)}`,
            message: `Are you sure you want to delete this ${activeTab.slice(0, -1)}?`,
            confirmLabel: "Delete",
            type: "danger"
        });

        if (!isConfirmed) return;

        try {
            switch (activeTab) {
                case 'polymers': await metadataService.deletePolymer(id); break;
                case 'solvents': await metadataService.deleteSolvent(id); break;
                case 'morphologies': await metadataService.deleteMorphology(id); break;
                case 'additives': await metadataService.deleteAdditive(id); break;
                case 'monomers': await metadataService.deleteMonomer(id); break;
            }
            setItems(prev => prev.filter(item => idOf(item) !== id));
            success("Deleted successfully");
        } catch (err) {
            notifyError(err.message || "Failed to delete");
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());
        if (activeTab === 'polymers') {
            // Copolymer fields: monomers as a list, no architecture/monomers for a homopolymer
            const copolymer = data.polymerType === 'COPOLYMER';
            data.architecture = copolymer && data.architecture ? data.architecture : null;
            data.monomers = copolymer ? (data.monomers || '').split(/[,:;\s]+/).map(m => m.trim()).filter(Boolean) : [];
        }

        try {
            if (currentItem) {
                const id = idOf(currentItem);
                let updated;
                switch (activeTab) {
                    case 'polymers': updated = await metadataService.updatePolymer(id, data); break;
                    case 'solvents': updated = await metadataService.updateSolvent(id, data); break;
                    case 'morphologies': updated = await metadataService.updateMorphology(id, data); break;
                    case 'additives': updated = await metadataService.updateAdditive(id, data); break;
                    case 'monomers': updated = await metadataService.updateMonomer(id, data); break;
                }
                setItems(prev => prev.map(item => (idOf(item) === id ? updated : item)));
            } else {
                let created;
                switch (activeTab) {
                    case 'polymers': created = await metadataService.createPolymer(data); break;
                    case 'solvents': created = await metadataService.createSolvent(data); break;
                    case 'morphologies': created = await metadataService.createMorphology(data); break;
                    case 'additives': created = await metadataService.createAdditive(data); break;
                    case 'monomers': created = await metadataService.createMonomer(data); break;
                }
                setItems(prev => [...prev, created]);
            }
            setShowModal(false);
            setCurrentItem(null);
        } catch (err) {
            notifyError(err.message || "Failed to save");
        }
    };

    const renderTable = () => {
        if (loading) return (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );

        if (error) return <div className="alert alert-danger">{error}</div>;

        return (
            <div className="table-responsive">
                <table className="admin-table">
                    <thead>
                        {activeTab === 'polymers' && (
                            <tr>
                                <th>Name</th>
                                <th>Full Name</th>
                                <th>Abbreviation</th>
                                <th>CAS</th>
                                <th>Manufacturer</th>
                                <th>MW</th>
                                <th>PDI</th>
                                <th>Density</th>
                                <th>CAT#</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        )}
                        {activeTab === 'solvents' && (
                            <tr>
                                <th>Name</th>
                                <th>Name</th>
                                <th>Abbreviation</th>
                                <th>CAS</th>
                                <th>Manufacturer</th>
                                <th>MW</th>
                                <th>Density</th>
                                <th>BP</th>
                                <th>CAT#</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        )}
                        {activeTab === 'morphologies' && (
                            <tr>
                                <th>Category</th>
                                <th>Label</th>
                                <th>Abbreviation</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        )}
                        {activeTab === 'additives' && (
                            <tr>
                                <th>Name</th>
                                <th>Full Name</th>
                                <th>Abbreviation</th>
                                <th>CAS</th>
                                <th>Category</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        )}
                        {activeTab === 'monomers' && (
                            <tr>
                                <th>Abbreviation</th>
                                <th>Name</th>
                                <th>CAS</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>No items found.</td></tr>
                        ) : (
                            items.map(item => {
                                const id = idOf(item);
                                return (
                                    <tr key={id}>
                                        {activeTab === 'polymers' && (
                                            <>
                                                <td className="fw-bold">
                                                    {item.polymerName}
                                                    {item.polymerType === 'COPOLYMER' && (
                                                        <div className="small fw-normal text-muted">
                                                            copolymer{item.architecture ? `, ${item.architecture.toLowerCase()}` : ''}{item.monomers?.length ? ` · ${item.monomers.join(':')}` : ''}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{item.fullName}</td>
                                                <td>{item.abbreviation}</td>
                                                <td>{item.casNumber}</td>
                                                <td><span className="badge bg-secondary-subtle text-secondary">{item.manufacturer || '-'}</span></td>
                                                <td>{item.molecularWeight ? `${item.molecularWeight} Da` : '-'}</td>
                                                <td>{item.pdi || '-'}</td>
                                                <td>{item.density ? `${item.density} g/cm³` : '-'}</td>
                                                <td style={{ fontSize: '12px' }}><code>{item.catalogNumber || '-'}</code></td>
                                            </>
                                        )}
                                        {activeTab === 'solvents' && (
                                            <>
                                                <td className="fw-bold">{item.solventName}</td>
                                                <td>{item.fullName}</td>
                                                <td>{item.abbreviation}</td>
                                                <td>{item.casNumber}</td>
                                                <td><span className="badge bg-secondary-subtle text-secondary">{item.manufacturer || '-'}</span></td>
                                                <td>{item.molecularWeight ? `${item.molecularWeight} g/mol` : '-'}</td>
                                                <td>{item.density ? `${item.density} g/cm³` : '-'}</td>
                                                <td>{item.boilingPoint ? `${item.boilingPoint} °C` : '-'}</td>
                                                <td style={{ fontSize: '12px' }}><code>{item.catalogNumber || '-'}</code></td>
                                            </>
                                        )}
                                        {activeTab === 'morphologies' && (
                                            <>
                                                <td><span className="badge bg-info-subtle text-info">{item.category}</span></td>
                                                <td className="fw-bold">{item.label}</td>
                                                <td>{item.abbreviation}</td>
                                            </>
                                        )}
                                        {activeTab === 'additives' && (
                                            <>
                                                <td className="fw-bold">{item.additiveName}</td>
                                                <td>{item.fullName}</td>
                                                <td>{item.abbreviation}</td>
                                                <td>{item.casNumber}</td>
                                                <td><span className="badge bg-info-subtle text-info">{(item.category || 'OTHER').toLowerCase()}</span></td>
                                            </>
                                        )}
                                        {activeTab === 'monomers' && (
                                            <>
                                                <td className="fw-bold">{item.abbreviation}</td>
                                                <td>{item.name}</td>
                                                <td>{item.casNumber}</td>
                                            </>
                                        )}
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                                <button className="btn btn-sm btn-outline-primary" onClick={() => { setCurrentItem(item); setShowModal(true); }}>
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(id)}>
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <AdminLayout
            title="Metadata Management"
            subtitle="Control polymers (and copolymers), solvents, morphologies, additives and monomers used across the system."
        >
            <div className="admin-card">
                <div className="admin-tabs" style={{ display: 'flex', borderBottom: '1px solid #dee2e6', marginBottom: '1.5rem', gap: '1rem' }}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-light'}`}
                            onClick={() => setActiveTab(tab.id)}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                        >
                            <i className={`bi ${tab.icon}`}></i> {tab.label}
                        </button>
                    ))}
                    {activeTab !== 'proposals' && (
                        <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
                            <button className="btn btn-success" onClick={() => { setCurrentItem(null); setShowModal(true); }}>
                                <i className="bi bi-plus-lg"></i> Add New {activeTab.slice(0, -1)}
                            </button>
                        </div>
                    )}
                </div>

                {activeTab === 'proposals' ? <VocabularyProposalsPanel /> : renderTable()}
            </div>

            {/* Consistent Styled Modal */}
            {showModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            padding: '24px',
                            borderRadius: '8px',
                            width: '600px',
                            maxWidth: '92vw',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}
                    >
                        <form onSubmit={handleSave}>
                            <h3 style={{ margin: 0, fontWeight: '600', fontSize: '20px', marginBottom: '16px' }}>
                                {currentItem ? 'Edit' : 'Add'} {activeTab.slice(0, -1).charAt(0).toUpperCase() + activeTab.slice(0, -1).slice(1)}
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {activeTab === 'polymers' && (
                                    <>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Polymer Name *</label>
                                            <input name="polymerName" defaultValue={currentItem?.polymerName} className="form-control" placeholder="e.g. PCL" required style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Full Name</label>
                                            <input name="fullName" defaultValue={currentItem?.fullName} className="form-control" placeholder="e.g. Polycaprolactone" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Abbreviation</label>
                                                <input name="abbreviation" defaultValue={currentItem?.abbreviation} className="form-control" placeholder="PCL" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>CAS Number</label>
                                                <input name="casNumber" defaultValue={currentItem?.casNumber} className="form-control" placeholder="24980-41-4" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Type</label>
                                                <select name="polymerType" defaultValue={currentItem?.polymerType || 'HOMOPOLYMER'} className="form-select" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}>
                                                    <option value="HOMOPOLYMER">Homopolymer</option>
                                                    <option value="COPOLYMER">Copolymer</option>
                                                </select>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Architecture (copolymers)</label>
                                                <select name="architecture" defaultValue={currentItem?.architecture || ''} className="form-select" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}>
                                                    <option value="">-</option>
                                                    <option value="RANDOM">Random</option>
                                                    <option value="ALTERNATING">Alternating</option>
                                                    <option value="BLOCK">Block</option>
                                                    <option value="GRAFT">Graft</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Monomers (copolymers, in composition order)</label>
                                            <input name="monomers" defaultValue={(currentItem?.monomers || []).join(', ')} className="form-control" placeholder="e.g. LA, GA" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            <span style={{ fontSize: '12px', color: '#666' }}>Abbreviations from the Monomers tab. A composition like 75:25 is read in this order. Leave empty if they vary by grade.</span>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Molecular Weight (Mw)</label>
                                                <input name="molecularWeight" type="number" step="any" defaultValue={currentItem?.molecularWeight} className="form-control" placeholder="e.g. 80000" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>PDI</label>
                                                <input name="pdi" type="number" step="any" defaultValue={currentItem?.pdi} className="form-control" placeholder="e.g. 1.2" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Manufacturer</label>
                                                <input name="manufacturer" defaultValue={currentItem?.manufacturer} className="form-control" placeholder="Supplier name" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Catalog Number</label>
                                                <input name="catalogNumber" defaultValue={currentItem?.catalogNumber} className="form-control" placeholder="Supplier's product number" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Density (g/cm³)</label>
                                            <input name="density" type="number" step="any" defaultValue={currentItem?.density} className="form-control" placeholder="e.g. 1.145" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                    </>
                                )}
                                {activeTab === 'additives' && (
                                    <>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Additive Name *</label>
                                            <input name="additiveName" defaultValue={currentItem?.additiveName} className="form-control" placeholder="e.g. AGNO3" required style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Full Name</label>
                                            <input name="fullName" defaultValue={currentItem?.fullName} className="form-control" placeholder="e.g. Silver Nitrate" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Abbreviation</label>
                                                <input name="abbreviation" defaultValue={currentItem?.abbreviation} className="form-control" placeholder="AgNO3" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>CAS Number</label>
                                                <input name="casNumber" defaultValue={currentItem?.casNumber} className="form-control" placeholder="7761-88-8" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Category</label>
                                            <select name="category" defaultValue={currentItem?.category || 'OTHER'} className="form-select" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}>
                                                <option value="NANOPARTICLE">Nanoparticle</option>
                                                <option value="SALT">Salt</option>
                                                <option value="ACTIVE">Drug / active</option>
                                                <option value="FILLER">Filler</option>
                                                <option value="SURFACTANT">Surfactant</option>
                                                <option value="PLASTICIZER">Plasticiser</option>
                                                <option value="CROSSLINKER">Crosslinker</option>
                                                <option value="OTHER">Other</option>
                                            </select>
                                        </div>
                                    </>
                                )}
                                {activeTab === 'monomers' && (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Abbreviation *</label>
                                                <input name="abbreviation" defaultValue={currentItem?.abbreviation} className="form-control" placeholder="e.g. TrFE" required style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>CAS Number</label>
                                                <input name="casNumber" defaultValue={currentItem?.casNumber} className="form-control" placeholder="359-11-5" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Name</label>
                                            <input name="name" defaultValue={currentItem?.name} className="form-control" placeholder="e.g. Trifluoroethylene" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                    </>
                                )}
                                {activeTab === 'solvents' && (
                                    <>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Solvent Name *</label>
                                            <input name="solventName" defaultValue={currentItem?.solventName} className="form-control" placeholder="e.g. DMF" required style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Full Name</label>
                                            <input name="fullName" defaultValue={currentItem?.fullName} className="form-control" placeholder="e.g. Dimethylformamide" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Abbreviation</label>
                                                <input name="abbreviation" defaultValue={currentItem?.abbreviation} className="form-control" placeholder="DMF" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>CAS Number</label>
                                                <input name="casNumber" defaultValue={currentItem?.casNumber} className="form-control" placeholder="68-12-2" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Molecular Weight (g/mol)</label>
                                                <input name="molecularWeight" type="number" step="any" defaultValue={currentItem?.molecularWeight} className="form-control" placeholder="e.g. 73.09" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Density (g/cm³)</label>
                                                <input name="density" type="number" step="any" defaultValue={currentItem?.density} className="form-control" placeholder="e.g. 0.944" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Manufacturer</label>
                                                <input name="manufacturer" defaultValue={currentItem?.manufacturer} className="form-control" placeholder="Supplier name" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Catalog Number</label>
                                                <input name="catalogNumber" defaultValue={currentItem?.catalogNumber} className="form-control" placeholder="Supplier's product number" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Boiling Point (°C)</label>
                                            <input name="boilingPoint" type="number" step="any" defaultValue={currentItem?.boilingPoint} className="form-control" placeholder="e.g. 153" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                    </>
                                )}
                                {activeTab === 'morphologies' && (
                                    <>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Category *</label>
                                            <select name="category" defaultValue={currentItem?.category || 'Structure'} className="form-select" required style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}>
                                                <option>Structure</option>
                                                <option>Topography</option>
                                                <option>Texture</option>
                                                <option>Shape</option>
                                                <option>Composition</option>
                                                <option>Defects</option>
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Label *</label>
                                            <input name="label" defaultValue={currentItem?.label} className="form-control" placeholder="e.g. Porous" required style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>Abbreviation</label>
                                            <input name="abbreviation" defaultValue={currentItem?.abbreviation} className="form-control" placeholder="P" style={{ padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }} />
                                        </div>
                                    </>
                                )}
                            </div>

                            <div
                                style={{
                                    marginTop: '24px',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '12px'
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); setCurrentItem(null); }}
                                    style={{
                                        backgroundColor: '#eee',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        color: '#333'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        backgroundColor: '#007bff',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '8px 16px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    {currentItem ? 'Save Changes' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default MetadataManagement;
